import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Users, 
  MapPin, 
  Link2, 
  Plus, 
  Trash2, 
  Copy, 
  Loader2,
  CheckCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationDesc, setNewLocationDesc] = useState('');
  const [newInviteLocation, setNewInviteLocation] = useState('');
  const [newInviteRole, setNewInviteRole] = useState('begeleider');
  const [newInviteDays, setNewInviteDays] = useState('7');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [locRes, usersRes, invitesRes] = await Promise.all([
        axios.get(`${API_URL}/api/locations`),
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/invites`)
      ]);
      setLocations(locRes.data);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      toast.error('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const createLocation = async () => {
    if (!newLocationName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/locations`, {
        name: newLocationName,
        description: newLocationDesc
      });
      toast.success('Locatie aangemaakt');
      setNewLocationName('');
      setNewLocationDesc('');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Kon locatie niet aanmaken');
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze locatie wilt verwijderen?')) return;
    try {
      await axios.delete(`${API_URL}/api/locations/${id}`);
      toast.success('Locatie verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon locatie niet verwijderen');
    }
  };

  const createInvite = async () => {
    if (!newInviteLocation) return;
    try {
      const response = await axios.post(`${API_URL}/api/invites`, {
        location_id: newInviteLocation,
        role: newInviteRole,
        expires_days: parseInt(newInviteDays)
      });
      toast.success('Uitnodigingslink aangemaakt');
      setInviteDialogOpen(false);
      fetchData();
      
      // Copy to clipboard
      const link = `${window.location.origin}/register?code=${response.data.code}`;
      navigator.clipboard.writeText(link);
      toast.info('Link gekopieerd naar klembord');
    } catch (error) {
      toast.error('Kon uitnodiging niet aanmaken');
    }
  };

  const deleteInvite = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/invites/${id}`);
      toast.success('Uitnodiging verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Kon uitnodiging niet verwijderen');
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?')) return;
    try {
      await axios.delete(`${API_URL}/api/users/${id}`);
      toast.success('Gebruiker verwijderd');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon gebruiker niet verwijderen');
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/register?code=${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Link gekopieerd naar klembord');
  };

  const getLocationName = (id) => {
    return locations.find(l => l.id === id)?.name || 'Onbekend';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Beheerder</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <Tabs defaultValue="locations" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="locations" data-testid="tab-locations">
              <MapPin className="w-4 h-4 mr-2" />
              Locaties
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Gebruikers
            </TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites">
              <Link2 className="w-4 h-4 mr-2" />
              Uitnodigingen
            </TabsTrigger>
          </TabsList>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Woonlocaties</h2>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-location-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuwe locatie
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe locatie toevoegen</DialogTitle>
                    <DialogDescription>
                      Maak een nieuwe woonlocatie aan voor het dashboard.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Naam</Label>
                      <Input
                        id="name"
                        value={newLocationName}
                        onChange={(e) => setNewLocationName(e.target.value)}
                        placeholder="Bijv. Groep Zwaluw"
                        data-testid="new-location-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Beschrijving (optioneel)</Label>
                      <Input
                        id="description"
                        value={newLocationDesc}
                        onChange={(e) => setNewLocationDesc(e.target.value)}
                        placeholder="Korte beschrijving"
                        data-testid="new-location-desc-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>
                      Annuleren
                    </Button>
                    <Button onClick={createLocation} data-testid="create-location-btn">
                      Aanmaken
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {locations.map((location, index) => (
                <motion.div
                  key={location.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card data-testid={`location-card-${location.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                          <MapPin className="w-6 h-6 text-primary" />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => deleteLocation(location.id)}
                          data-testid={`delete-location-${location.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <CardTitle>{location.name}</CardTitle>
                      <CardDescription>{location.description || 'Geen beschrijving'}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-muted-foreground">
                        Code: <span className="font-mono font-medium">{location.invite_code}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Gebruikers ({users.length})</h2>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-4 font-medium">Naam</th>
                        <th className="text-left p-4 font-medium">E-mail</th>
                        <th className="text-left p-4 font-medium">Rol</th>
                        <th className="text-left p-4 font-medium">Locatie</th>
                        <th className="text-left p-4 font-medium">2FA</th>
                        <th className="p-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b last:border-0" data-testid={`user-row-${u.id}`}>
                          <td className="p-4 font-medium">{u.name}</td>
                          <td className="p-4 text-muted-foreground">{u.email}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              u.role === 'admin' ? 'bg-primary/10 text-primary' :
                              u.role === 'zzp' ? 'bg-accent/10 text-accent' :
                              'bg-secondary text-secondary-foreground'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="p-4 text-muted-foreground">
                            {u.location_id ? getLocationName(u.location_id) : '-'}
                          </td>
                          <td className="p-4">
                            {u.totp_enabled ? (
                              <CheckCircle className="w-4 h-4 text-success" />
                            ) : (
                              <span className="text-muted-foreground text-sm">Uit</span>
                            )}
                          </td>
                          <td className="p-4">
                            {u.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => deleteUser(u.id)}
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invites Tab */}
          <TabsContent value="invites">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Uitnodigingslinks</h2>
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-invite-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuwe uitnodiging
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Uitnodigingslink aanmaken</DialogTitle>
                    <DialogDescription>
                      Maak een uitnodigingslink aan voor een nieuwe medewerker.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Locatie</Label>
                      <Select value={newInviteLocation} onValueChange={setNewInviteLocation}>
                        <SelectTrigger data-testid="invite-location-select">
                          <SelectValue placeholder="Selecteer locatie" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((loc) => (
                            <SelectItem key={loc.id} value={loc.id}>
                              {loc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Rol</Label>
                      <Select value={newInviteRole} onValueChange={setNewInviteRole}>
                        <SelectTrigger data-testid="invite-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="begeleider">Begeleider</SelectItem>
                          <SelectItem value="zzp">ZZP&apos;er</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Geldig voor</Label>
                      <Select value={newInviteDays} onValueChange={setNewInviteDays}>
                        <SelectTrigger data-testid="invite-days-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 dag</SelectItem>
                          <SelectItem value="7">7 dagen</SelectItem>
                          <SelectItem value="30">30 dagen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                      Annuleren
                    </Button>
                    <Button onClick={createInvite} disabled={!newInviteLocation} data-testid="create-invite-btn">
                      Aanmaken & Kopiëren
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {invites.map((invite, index) => (
                <motion.div
                  key={invite.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={invite.used ? 'opacity-60' : ''} data-testid={`invite-card-${invite.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                          <Link2 className="w-6 h-6 text-accent" />
                        </div>
                        <div className="flex gap-2">
                          {!invite.used && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => copyInviteLink(invite.code)}
                              data-testid={`copy-invite-${invite.id}`}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deleteInvite(invite.id)}
                            data-testid={`delete-invite-${invite.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-lg font-bold">{invite.code}</span>
                          {invite.used ? (
                            <span className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                              Gebruikt
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs bg-success/10 text-success">
                              Actief
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {getLocationName(invite.location_id)} • {invite.role}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          Verloopt: {new Date(invite.expires_at).toLocaleDateString('nl-NL')}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}

              {invites.length === 0 && (
                <div className="col-span-2 text-center py-12 text-muted-foreground">
                  Nog geen uitnodigingslinks aangemaakt.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
